import { FormikHelpers } from 'formik';
import { Dispatch } from 'react';
import {
    BusinessStepsOrder,
    BusinessVerificationStage,
    getNextStage,
} from '../BusinessVerificationV3Models';
import {
    FormValues,
    normalizeOperationsInformationData,
    OwnerTreeStructure,
    resolveMonthlyVolumeUsdId,
} from '../schema';
import { endpoints } from 'endpoints.config';
import instance from 'api';
import { setVerificationStage } from 'reducers/verification';
import { toast } from 'react-toastify';
import {
    Errors,
    processedErrors,
} from 'components/businessVerification/helpers/ExitContinueButtons';
import { DocumentFieldValue } from 'components/businessVerification/components/BusinessVerificationDocuments';
import { format } from 'date-fns';
import logger from 'helpers/logger';
import { ERROR_CODES } from '../../../errors';

/* 
Formats the values we submit from the form.
Returns a new object without mutating the existing object
Notably we need to remove any documents in the documentsUpload section that have not been uploaded(filename==null)
*/
const commaSeparatedSelectionIncludesUS = (value: string | null | undefined): boolean => {
    if (!value) return false;
    return value
        .split(',')
        .map((s) => s.trim())
        .includes('US');
};

const formatDateValue = (value: any): string | null => {
    if (!value) return null;
    // Value might come in as a Date object, string, or Formik '' placeholder
    if (typeof value === 'string') {
        if (value.trim() === '') return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
            return value.trim();
        }
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : format(parsed, 'yyyy-MM-dd');
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : format(parsed, 'yyyy-MM-dd');
};

const normalizeNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const pruneIncompleteTreeNodes = (
    nodes: OwnerTreeStructure[] | null | undefined
): OwnerTreeStructure[] => {
    if (!Array.isArray(nodes)) return [];
    return nodes
        .filter((node) => !node.isNewNode)
        .map((node) => ({
            ...node,
            children: node.children ? pruneIncompleteTreeNodes(node.children) : node.children,
        }));
};

const flattenTreeGuids = (nodes: OwnerTreeStructure[]): Set<string> => {
    const guids = new Set<string>();
    const walk = (list: OwnerTreeStructure[]) => {
        list.forEach((node) => {
            guids.add(node.ownersGuid);
            if (node.children) walk(node.children);
        });
    };
    walk(nodes);
    return guids;
};

/**
 * Discards any beneficial owner the user started adding (via "Add Controlling Party" /
 * "Add business/individual beneficial owner") but never completed. Left in place, an
 * incomplete owner has empty required fields that the server rejects with a 400 on
 * Save & Exit, and fails client-side validation with a confusing "Owner Data Required"
 * message on Save & Continue even though every real, saved owner is complete.
 */
export const pruneIncompleteOwners = (values: FormValues): FormValues => {
    const originalTree = values.ownerInformation?.beneficialOwnersStructure ?? [];
    const prunedTree = pruneIncompleteTreeNodes(originalTree);

    const remainingGuids = flattenTreeGuids(prunedTree);
    return {
        ...values,
        ownerInformation: {
            ...values.ownerInformation,
            beneficialOwnersStructure: prunedTree,
            individualBeneficialOwners: (
                values.ownerInformation?.individualBeneficialOwners ?? []
            ).filter((owner) => remainingGuids.has(owner.guid)),
            businessBeneficialOwners: (
                values.ownerInformation?.businessBeneficialOwners ?? []
            ).filter((owner) => remainingGuids.has(owner.guid)),
        },
    };
};

export const formatValues = (
    _values: FormValues,
    currentStage?: BusinessVerificationStage | null
) => {
    const { registrationInformation, operationsInformation } = _values;
    const normalizedOperations = normalizeOperationsInformationData(operationsInformation);
    const monthlyVolumeUsdId = resolveMonthlyVolumeUsdId(operationsInformation);
    const values = {
        ..._values,
        registrationInformation: {
            ...registrationInformation,
            registrationDate: formatDateValue(registrationInformation.registrationDate),
            taxNumber: registrationInformation.taxNumber,
        },
        operationsInformation: {
            ...normalizedOperations,
            anticipatedMonthlyTransactionCountId: normalizeNullableNumber(
                normalizedOperations.anticipatedMonthlyTransactionCountId
            ),
            // Single canonical API field for Monthly USD Value (Banking & Payments).
            anticipatedMonthlyVolumeUsdId: monthlyVolumeUsdId,
            customerTrafficCountries: Array.isArray(normalizedOperations.customerTrafficCountries)
                ? normalizedOperations.customerTrafficCountries.join(', ')
                : normalizedOperations.customerTrafficCountries ?? null,
            activeBanks: normalizedOperations.activeBanks
                ? normalizedOperations.activeBanks.trim() || null
                : null,
            // Legacy V2 field — cleared on submit so the value is not sent twice.
            monthlyUsdValueId: null,
            bFundsReceivedUSA: commaSeparatedSelectionIncludesUS(
                normalizedOperations.countriesReceivingPaymentsFrom
            ),
            bFundsSentUSA: commaSeparatedSelectionIncludesUS(
                normalizedOperations.countriesSendingPaymentsTo
            ),
        },
        ownerInformation: {
            ..._values.ownerInformation,
            individualBeneficialOwners: (
                _values.ownerInformation?.individualBeneficialOwners ?? []
            ).map((owner) => ({
                ...owner,
                dateOfBirth: formatDateValue(owner.dateOfBirth),
                ssn: owner.ssn,
            })),
            businessBeneficialOwners: (
                _values.ownerInformation?.businessBeneficialOwners ?? []
            ).map(({ exemptionId: _omit, ...owner }: any) => ({
                ...owner,
                registrationDate: formatDateValue(owner.registrationDate),
                taxNumber: owner.taxNumber,
            })),
        },
    };
    if (Array.isArray(values.documentsUpload) && values.documentsUpload.length > 1)
        values.documentsUpload = values.documentsUpload.filter((document) => document.fileName);

    const result = values as typeof values & {
        businessVerificationStep?: BusinessVerificationStage;
        businessVerificationNextStep?: BusinessVerificationStage;
    };
    if (currentStage != null) {
        result.businessVerificationStep = currentStage;
        result.businessVerificationNextStep = getNextStage(currentStage);
    }
    return result;
};

export const submitForm = async (
    values: any,
    helpers: any,
    dispatch: Dispatch<any>,
    setApiErrors: React.SetStateAction<any>
) => {
    try {
        const response = await instance.post(endpoints.businessVerificationV3Module.submitForm, {
            ...formatValues(values as any),
            businessVerificationStep: BusinessVerificationStage.Complete,
        });

        // Check for listInnerErrors immediately after response - show toast if any exist
        const listInnerErrors = response?.data?.details?.listInnerErrors;
        const hasListInnerErrors = Array.isArray(listInnerErrors) && listInnerErrors.length > 0;

        if (hasListInnerErrors) {
            toast.error('Form Submission Failed');
        }

        if (response.status === 200 && response.data && response.data.status === `1`) {
            if (listInnerErrors && Array.isArray(listInnerErrors) && listInnerErrors.length === 0) {
                dispatch(
                    setVerificationStage({ currentStage: BusinessVerificationStage.Complete })
                );
            } else if (hasListInnerErrors) {
                //if errors check which stage and set step to the earliest in the process
                let errorsOnDifferentStage = listInnerErrors
                    .map((error: Errors) => getStageFromFieldName(error.fieldName))
                    .filter(
                        (
                            stage: BusinessVerificationStage | null
                        ): stage is BusinessVerificationStage =>
                            stage != null && stage !== BusinessVerificationStage.Documents
                    );
                if (Array.isArray(errorsOnDifferentStage) && errorsOnDifferentStage.length > 0) {
                    for (const stage of BusinessStepsOrder) {
                        if (stage === BusinessVerificationStage.Documents) continue;
                        if (errorsOnDifferentStage.includes(stage)) {
                            setResponseErrors(
                                listInnerErrors,
                                helpers,
                                stage,
                                setApiErrors,
                                values
                            );
                            dispatch(
                                setVerificationStage({
                                    currentStage: stage,
                                })
                            );
                            break;
                        }
                    }
                } else {
                    setResponseErrors(
                        listInnerErrors,
                        helpers,
                        BusinessVerificationStage.Documents,
                        setApiErrors,
                        values
                    );
                }
            }
        } else {
            // API returned 200 but status !== '1' (e.g. validation/business error)
            toast.error('Form Submission Failed');
        }
    } catch (error: any) {
        toast.error('Form Submission Failed');
    }
};

const getStageFromFieldName = (
    fieldName: string,
    divider = '_'
): BusinessVerificationStage | null => {
    const stageName = fieldName.split(divider)[0];
    switch (stageName) {
        case 'RegistrationInformation':
            return BusinessVerificationStage.RegistrationInformation;
        case 'OperationsInformation':
            return BusinessVerificationStage.OperationsInformation;
        case 'OwnerInformation':
            return BusinessVerificationStage.OwnerInformation;
        case 'Terms':
            return BusinessVerificationStage.Terms;
        case 'DocumentsUpload':
            return BusinessVerificationStage.Documents;
        default:
            return null;
    }
};

export async function saveContinue<T>(
    values: T,
    helpers: FormikHelpers<T>,
    dispatch: Dispatch<any>,
    stage: BusinessVerificationStage | null,
    setSubmitValues: React.SetStateAction<any>,
    setApiErrors: React.SetStateAction<any>
) {
    try {
        if (stage) {
            const response = await instance.post(
                endpoints.businessVerificationV3Module.saveFormData,
                {
                    ...formatValues(values as any as FormValues),
                    businessVerificationStep: stage,
                    businessVerificationNextStep: getNextStage(stage),
                }
            );
            if (response.status === 200 && response.data && response.data.status === `1`) {
                if (
                    response.data.details.listInnerErrors &&
                    Array.isArray(response.data.details.listInnerErrors) &&
                    response.data.details.listInnerErrors.length === 0
                ) {
                    //if no errors proceed
                    helpers.setFieldValue('businessVerificationStep', getNextStage(stage));
                    dispatch(
                        setVerificationStage({
                            currentStage: getNextStage(stage),
                        })
                    );
                    helpers.setTouched({});
                    setApiErrors([]);
                } else if (
                    response.data.details.listInnerErrors &&
                    Array.isArray(response.data.details.listInnerErrors) &&
                    response.data.details.listInnerErrors.length > 0
                ) {
                    toast.error('Error saving form, please try again.');
                    helpers.setFieldValue('businessVerificationStep', stage);
                    setSubmitValues({ ...values });
                    setResponseErrors(
                        response.data.details.listInnerErrors,
                        helpers,
                        stage,
                        setApiErrors,
                        values
                    );
                    document
                        .querySelector('.BusinessVerificationModalBg')
                        ?.scrollTo({ top: 0, behavior: 'smooth' });
                }
            } else {
                const { errors } = response.data;
                if (errors.length > 0) {
                    setSubmitValues({ ...values });
                    setResponseErrors(errors, helpers, stage, setApiErrors, values);
                }
            }
        }
    } catch (error: any) {
        logger.log(error);
        // Show error toast when save fails
        toast.error('Error saving form, please try again.');
    }
    // }
}

const parseDotFormat = (name: string, stage: BusinessVerificationStage) => {
    // OwnerInformation.BeneficialOwnersStructure[1].Children
    const words = name.split('.').map((word) => word.charAt(0).toLowerCase() + word.slice(1));
    if ((getStageFromFieldName(name) === stage, '.')) {
        return words.join('.');
    }
};
const parseUnderscoreFormat = (name: string, stage: BusinessVerificationStage) => {
    const words = name.split('_').map((word) => word.charAt(0).toLowerCase() + word.slice(1));
    if (getStageFromFieldName(name) === stage) {
        if (words.length === 1) {
            return words[0];
        } else if (words.length === 2) {
            return `${words[0]}.${words[1]}`;
        } else if (words.length === 3) {
            let index = +words[2] - 1;
            return `${words[0]}.${index}.${words[1]}`;
        } else {
            let index = +words[3] - 1;
            return `${words[0]}.${words[1]}.${index}.${words[2]}`;
        }
    }
};

export const parseFieldName = (name: string, stage: BusinessVerificationStage) => {
    if (name.includes('.')) return parseDotFormat(name, stage);
    else return parseUnderscoreFormat(name, stage);
};

const parseDocumentUploadResponse = (error: Errors, values: any) => {
    const words = error.fieldName
        .split('_')
        .map((word) => word.charAt(0).toLowerCase() + word.slice(1));
    let errorMessage = '';
    if (words.length === 2) {
        if (words[1] === 'documentTypesID') {
            errorMessage =
                error.messageCode === 'Missing'
                    ? 'Document type missing please try again'
                    : 'Only one file per document type is allowed';
        } else {
            errorMessage = 'File names must be distinct';
        }
        return [words[0], errorMessage];
    }
    if (words.length === 3) {
        let index = values.documentsUpload
            .map((upload: DocumentFieldValue) => upload.documentTypesID)
            .indexOf(+words[2]);
        if (index >= 0) {
            errorMessage = 'Cannot find file please reupload';
            return [`${words[0]}.${index}`, errorMessage];
        }
    }

    return [null, null];
};

export const setResponseErrors = (
    errors: Errors[],
    helpers: any,
    stage: BusinessVerificationStage,
    setApiErrors: React.SetStateAction<any>,
    values?: any
) => {
    const apiErrors: processedErrors = [];
    errors.forEach((error: any, index: number) => {
        if (stage === BusinessVerificationStage.Documents && values) {
            let [fieldName, errorMessage] = parseDocumentUploadResponse(error, values);
            if (fieldName && errorMessage) {
                apiErrors.push({ fieldName: fieldName, message: errorMessage });
                helpers.setFieldError(`${fieldName}`, errorMessage);
            }
        } else {
            let fieldName = parseFieldName(error.fieldName, stage);
            if (fieldName) {
                if (
                    typeof error.messageCode === 'string' &&
                    ERROR_CODES[error.messageCode as keyof typeof ERROR_CODES]
                ) {
                    const msg = ERROR_CODES[error.messageCode as keyof typeof ERROR_CODES];
                    apiErrors.push({
                        fieldName: fieldName,
                        message: msg,
                    });
                    helpers.setFieldError(`${fieldName}`, msg);
                } else if (error.messageCode === 'Invalid') {
                    apiErrors.push({
                        fieldName: fieldName,
                        message: 'Please provide valid information',
                    });
                    helpers.setFieldError(`${fieldName}`, 'Please provide valid information');
                } else {
                    apiErrors.push({ fieldName: fieldName, message: '*Required' });
                    helpers.setFieldError(`${fieldName}`, '*Required');
                }
            }
        }
    });
    if (apiErrors.length > 0) {
        setApiErrors([...apiErrors]);
    }
};
